import React, { cloneElement, useState, useEffect, useMemo } from 'react';
import { Space, Typography, theme, notification } from 'antd';
import { _T } from "../../../../../base-react/src/Localization";
import { hooks, models, apis, helpers } from '@vote4dance/judging-sdk';
import { useDndMonitor } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
var Stream = hooks.streams.Stream;
var ScheduleRoundSource = models.rounds.ScheduleRoundSource;
var Api = apis.Api;
var groupRoundListIntoChildren = helpers.groupRoundListIntoChildren;
import { flattenTree, moveRoundInArray } from '../utilities';
import { RoundTable } from '../../../../components/RoundTable';
import { ScheduleAssignBeats } from './ScheduleAssignBeats';
import { ScheduleMoveTo } from './ScheduleMoveTo';
const { Text } = Typography;
const { useToken } = theme;
export const ScheduleRoundsTable = (props) => {
    const { event_id, schedule_id, disabled } = props;
    const [expandedRowKeys, setExpandedRowKeys] = useState([]);
    const [rounds, loadingRounds] = Stream(ScheduleRoundSource(event_id, schedule_id));
    const [tableRounds, setTableRounds] = useState([]);
    const { token } = useToken();
    useDndMonitor({
        onDragStart: handleDragStart,
        onDragOver: handleDragOver,
        onDragEnd: handleDragEnd,
        onDragCancel: handleDragCancel
    });
    useEffect(() => {
        setTableRounds(rounds ? groupRoundListIntoChildren(rounds) : []);
    }, [rounds]);
    const flattenedItems = useMemo(() => {
        if (tableRounds.length > 0) {
            const flattenedTree = flattenTree(tableRounds);
            return flattenedTree;
        }
    }, [tableRounds]);
    const sortedIds = useMemo(() => flattenedItems ? flattenedItems.map((a) => a.rowKey) : [], [
        flattenedItems,
    ]);
    function handleDragStart({ active }) {
        var _a, _b;
        if (((_b = (_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.type) != 'round') {
            return;
        }
        setExpandedRowKeys(expandedRowKeys.filter(key => key != active.id));
    }
    function handleDragOver({ active, over }) {
        var _a, _b, _c, _d, _e, _f;
        if (((_b = (_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.type) != 'round') {
            return;
        }
        const activeRound = (_d = (_c = active.data) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.round;
        // Move to this list
        if (over && ((_f = (_e = over.data) === null || _e === void 0 ? void 0 : _e.current) === null || _f === void 0 ? void 0 : _f.sortable.containerId) === schedule_id) {
            if (activeRound && !sortedIds.includes(active.id)) {
                setTableRounds(items => ([
                    ...items,
                    activeRound
                ]));
            }
        }
        else {
            if (sortedIds.includes(active.id)) {
                if (activeRound && activeRound.children && activeRound.children.length > 0) {
                    setTableRounds(items => items.filter(item => item.rowKey !== active.id));
                }
                else {
                    setTableRounds(groupRoundListIntoChildren(flattenedItems.filter(item => item.rowKey !== active.id)));
                }
            }
        }
    }
    function handleDragEnd({ active, over }) {
        var _a, _b, _c, _d;
        if (((_b = (_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.type) != 'round') {
            return;
        }
        if (!over) {
            setTableRounds(rounds ? groupRoundListIntoChildren(rounds) : []);
            return;
        }
        if (((_d = (_c = over === null || over === void 0 ? void 0 : over.data) === null || _c === void 0 ? void 0 : _c.current) === null || _d === void 0 ? void 0 : _d.sortable.containerId) === schedule_id) {
            if (flattenedItems) {
                const activeIndex = flattenedItems.findIndex((item) => item.rowKey === active.id);
                const overIndex = flattenedItems.findIndex((item) => item.rowKey === (over === null || over === void 0 ? void 0 : over.id));
                const oldArrayIds = flattenedItems.filter(round => !round.isPlaceholder).map(round => round.rowKey);
                const movingRound = flattenedItems.find(item => item.rowKey === active.id);
                const numMovingChildren = (movingRound && movingRound.children) ? movingRound.children.length : 0;
                const newArray = moveRoundInArray(flattenedItems, activeIndex, overIndex, numMovingChildren, expandedRowKeys);
                const newArrayIds = newArray.filter(round => !round.isPlaceholder).map(round => round.rowKey);
                // Check if nothing changed
                if (JSON.stringify(oldArrayIds) == JSON.stringify(newArrayIds)) {
                    return;
                }
                // Update client only
                const newList = groupRoundListIntoChildren(newArray);
                setTableRounds(newList);
                // Update on server
                Api.call(ScheduleRoundSource(event_id, schedule_id).url, "set-order", newArrayIds)
                    .catch((err) => {
                    notification.error({
                        message: err.message
                    });
                    // Reset list
                    const newList = rounds ? groupRoundListIntoChildren(rounds) : [];
                    setTableRounds(newList);
                });
            }
        }
    }
    function handleDragCancel({ active }) {
        var _a, _b;
        if (((_b = (_a = active === null || active === void 0 ? void 0 : active.data) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.type) != 'round') {
            return;
        }
        setTableRounds(rounds ? groupRoundListIntoChildren(rounds) : []);
    }
    const DraggableRow = ({ children, className, style: styleFromProps, ...remainingProps }) => {
        const round = flattenedItems && flattenedItems.find((round) => round.rowKey === remainingProps["data-row-key"]);
        const { setNodeRef, attributes, isDragging, listeners, transition, transform } = useSortable({
            id: remainingProps["data-row-key"],
            data: {
                type: 'round',
                round: round
            },
        });
        const style = {
            ...styleFromProps,
            transition,
            transform: CSS.Transform.toString(transform),
            opacity: isDragging ? 0.5 : undefined,
        };
        // create copy of children, don't mutate original children list !
        const copyOfChildren = [...children];
        if (copyOfChildren.length > 0) {
            const indexOfSortHandle = copyOfChildren.findIndex((child) => child.key === "sort");
            if (indexOfSortHandle >= 0) {
                // clone sort handle element and add listeners and attributes
                const sortTriggererHandleCell = cloneElement(copyOfChildren[indexOfSortHandle], {
                    additionalProps: { ...attributes, ...listeners }
                });
                // replace old element with new clone
                copyOfChildren.splice(indexOfSortHandle, 1, sortTriggererHandleCell);
            }
        }
        return (React.createElement("tr", { ref: setNodeRef, className: className, ...remainingProps, style: style }, copyOfChildren));
    };
    const components = {
        body: {
            row: DraggableRow
        }
    };
    const footer = (selection) => {
        return (React.createElement(Space, null,
            React.createElement(ScheduleAssignBeats, { event_id: event_id, selection: selection, disabled: disabled }),
            React.createElement(ScheduleMoveTo, { event_id: event_id, selection: selection, disabled: disabled })));
    };
    const memoizedRoundTable = useMemo(() => (React.createElement(RoundTable, { event_id: event_id, components: components, data: tableRounds, loading: loadingRounds, footer: footer, isSortable: true, disabled: disabled })), [tableRounds, loadingRounds, disabled]);
    const scheduleSummary = useMemo(() => {
        if (!flattenedItems || flattenedItems.length === 0) {
            return null;
        }
        const validRounds = flattenedItems.filter((item) => !item.isPlaceholder);
        if (validRounds.length === 0) {
            return null;
        }
        const leafRounds = validRounds.filter((item) => !item.children || item.children.length === 0);
        const roundsForSummary = leafRounds.length > 0 ? leafRounds : validRounds;
        const lastPlannedRound = [...roundsForSummary].reverse().find((item) => item.round_start_time);
        const lastEstimatedRound = [...roundsForSummary].reverse().find((item) => item.round_estimated_time);
        const plannedEndRaw = lastPlannedRound === null || lastPlannedRound === void 0 ? void 0 : lastPlannedRound.round_start_time;
        const estimatedEndRaw = lastEstimatedRound === null || lastEstimatedRound === void 0 ? void 0 : lastEstimatedRound.round_estimated_time;
        const totalDelaySeconds = roundsForSummary.reduce((sum, item) => {
            const value = Number(item.round_overrun_seconds);
            return Number.isNaN(value) ? sum : sum + value;
        }, 0);
        const addSecondsToTime = (timeValue, seconds) => {
            const parts = timeValue.split(':').map(part => Number(part));
            if (parts.length < 2 || parts.some(part => Number.isNaN(part))) {
                return timeValue;
            }
            const [hours, minutes, secs = 0] = parts;
            const totalSeconds = hours * 3600 + minutes * 60 + secs + seconds;
            const normalized = ((totalSeconds % 86400) + 86400) % 86400;
            const nextHours = Math.floor(normalized / 3600);
            const nextMinutes = Math.floor((normalized % 3600) / 60);
            const paddedHours = String(nextHours).padStart(2, '0');
            const paddedMinutes = String(nextMinutes).padStart(2, '0');
            return `${paddedHours}:${paddedMinutes}`;
        };
        const plannedHeats = Number(lastPlannedRound === null || lastPlannedRound === void 0 ? void 0 : lastPlannedRound.round_num_heats);
        const estimatedHeats = Number(lastEstimatedRound === null || lastEstimatedRound === void 0 ? void 0 : lastEstimatedRound.round_num_heats);
        const plannedLengthSeconds = Number(lastPlannedRound === null || lastPlannedRound === void 0 ? void 0 : lastPlannedRound.round_heat_length) * (Number.isNaN(plannedHeats) ? 1 : plannedHeats);
        const estimatedLengthSeconds = Number(lastEstimatedRound === null || lastEstimatedRound === void 0 ? void 0 : lastEstimatedRound.round_heat_length) * (Number.isNaN(estimatedHeats) ? 1 : estimatedHeats);
        const plannedEnd = plannedEndRaw
            ? addSecondsToTime(plannedEndRaw, Number.isNaN(plannedLengthSeconds) ? 0 : plannedLengthSeconds)
            : undefined;
        const delayNumber = Number.isFinite(totalDelaySeconds) ? totalDelaySeconds : undefined;
        const formatDelay = (value) => {
            const sign = value > 0 ? '+' : value < 0 ? '-' : '';
            const abs = Math.abs(value);
            const hours = Math.floor(abs / 3600);
            const minutes = Math.floor((abs % 3600) / 60);
            const seconds = abs % 60;
            const paddedMinutes = String(minutes).padStart(2, '0');
            const paddedSeconds = String(seconds).padStart(2, '0');
            if (hours > 0) {
                const paddedHours = String(hours).padStart(2, '0');
                return `${sign}${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
            }
            return `${sign}${paddedMinutes}:${paddedSeconds}`;
        };
        const hasTimeBase = !!plannedEndRaw || !!estimatedEndRaw;
        const estimatedEnd = delayNumber && delayNumber !== 0 && estimatedEndRaw
            ? addSecondsToTime(estimatedEndRaw, Number.isNaN(estimatedLengthSeconds) ? 0 : estimatedLengthSeconds)
            : undefined;
        const delayLabel = hasTimeBase && delayNumber && delayNumber !== 0 ? formatDelay(delayNumber) : undefined;
        const delayStyle = delayNumber && delayNumber > 0
            ? { color: token.colorError }
            : { color: token.colorSuccess };
        if (!plannedEnd && !estimatedEnd && !delayLabel) {
            return null;
        }
        return {
            plannedEnd,
            estimatedEnd,
            delayLabel,
            delayStyle
        };
    }, [flattenedItems, token.colorError, token.colorSuccess]);
    if (sortedIds.length == 0) {
        return null;
    }
    return (React.createElement(SortableContext, { id: schedule_id, items: sortedIds, disabled: disabled, strategy: verticalListSortingStrategy },
        React.createElement("div", { style: { marginTop: 8 } },
            memoizedRoundTable,
            scheduleSummary && (React.createElement(Space, { size: "large", style: { margin: 12 } },
                scheduleSummary.plannedEnd && (React.createElement(Text, { type: "secondary" },
                    _T('schedule.footer.planned_end'),
                    ": ",
                    scheduleSummary.plannedEnd)),
                scheduleSummary.estimatedEnd && (React.createElement(Text, { type: "secondary" },
                    _T('schedule.footer.estimated_end'),
                    ": ",
                    scheduleSummary.estimatedEnd)),
                scheduleSummary.delayLabel && (React.createElement(Text, { type: "secondary" },
                    _T('schedule.footer.total_delay'),
                    ": ",
                    React.createElement("span", { style: scheduleSummary.delayStyle }, scheduleSummary.delayLabel))))))));
};
